import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiUsers } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useCommunities from '../../../../hooks/community/useCommunities';
import { setSelectedCommunity } from '../../../../redux/slices/applicationSlice';
import Community from '../../../../types/community';
import CreateCommunityModal from '../../../../components/modals/CreateCommunityModal';

interface CommunityCardProps {
  community: Community;
  onClick: (community: Community) => void;
}

function CommunityCard({ community, onClick }: CommunityCardProps) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div className="relative mx-auto w-full max-w-full sm:max-w-2xl">
      <div className="w-full overflow-hidden rounded-lg bg-secondary-50">
        <div
          role="button"
          tabIndex={0}
          onClick={() => onClick(community)}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick(community);
            }
          }}
          className="flex w-full flex-col items-start gap-4 p-4 text-left sm:flex-row sm:items-center">
          {/* Community Image */}
          {community.picture ? (
            <img
              src={community.picture}
              alt={community.name}
              className="h-48 w-full rounded-lg object-cover sm:h-40 sm:w-40 sm:flex-shrink-0"
            />
          ) : (
            <div className="flex h-48 w-full items-center justify-center rounded-lg bg-primary/10 sm:h-40 sm:w-40 sm:flex-shrink-0">
              <span className="text-2xl font-bold text-primary sm:text-3xl">
                {community.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Community Info */}
          <div className="mt-2 flex flex-1 flex-col gap-2 sm:mt-0">
            <h3 className="text-base font-semibold text-primary sm:text-lg">{community.name}</h3>
            {community.description && <p className="line-clamp-3 text-sm text-details/70">{community.description}</p>}
            <div className="flex items-center gap-2 text-sm text-details/70">
              <HiUsers className="h-4 w-4 flex-shrink-0" />
              <span>{t('page.dashboard.community.members', { count: (community as any).memberCount || 0 })}</span>
            </div>
            {community.role && community.role.toLowerCase() !== 'member' && (
              <span className="inline-block self-start rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {community.role.charAt(0).toUpperCase() + community.role.slice(1).toLowerCase()}
              </span>
            )}
          </div>

          {/* Arrow Icon (desktop) */}
          <div className="ml-auto hidden h-10 w-10 flex-shrink-0 items-center justify-center self-center rounded-full bg-primary/10 sm:ml-0 sm:flex">
            <svg className="h-5 w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        <div className="px-4 py-3 sm:hidden">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClick(community);
            }}
            className="w-full rounded-lg bg-primary px-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary/50">
            {t('general.open', { defaultValue: 'Open' })}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CommunitiesListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const communities = useCommunities();
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (communities !== undefined) {
      setIsLoading(false);
    }
  }, [communities]);

  const handleCommunityClick = (community: Community) => {
    dispatch(setSelectedCommunity(community.id));
    navigate('/community');
  };

  const handleCreateNew = () => {
    setShowCreateModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 sm:p-8">
        <p className="text-secondary-600">{t('page.dashboard.community.loading')}</p>
      </div>
    );
  }

  if (!communities || communities.length === 0) {
    return (
      <div className="flex min-h-[75vh] flex-col items-center justify-center gap-3 p-4 sm:p-6">
        <HiUsers className="h-8 w-8 text-primary" />
        <h2 className="text-xl font-semibold text-gray-900">{t('page.dashboard.communities.noCommunities')}</h2>
        <p className="text-center text-sm text-gray-600">{t('page.dashboard.communities.noCommunitiesDescription')}</p>
        <button
          type="button"
          onClick={handleCreateNew}
          className="rounded-md bg-primary px-4 py-2 text-sm text-white transition-colors hover:bg-opacity-90">
          {t('page.communityOverview.new')}
        </button>

        <CreateCommunityModal
          open={showCreateModal}
          setOpen={setShowCreateModal}
          onSuccess={() => {
            setShowCreateModal(false);
            navigate('/community');
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col p-4 sm:p-8">
      {/* Header */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="mb-2 text-2xl font-bold text-primary sm:text-3xl">{t('general.communities')}</h1>
        </div>
        <div className="flex w-full flex-col items-start gap-4 sm:w-auto sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={handleCreateNew}
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-50 text-primary-300 sm:h-12 sm:w-12">
            <HiPlus className="inline-block h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Communities Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {communities.map((community) => (
          <div key={community.id} className="flex justify-center">
            <CommunityCard community={community} onClick={handleCommunityClick} />
          </div>
        ))}
      </div>

      <CreateCommunityModal
        open={showCreateModal}
        setOpen={setShowCreateModal}
        onSuccess={() => {
          setShowCreateModal(false);
          navigate('/community');
        }}
      />
    </div>
  );
}
